import { createFileRoute } from "@tanstack/react-router";
import { Binoculars, Sparkles, Filter } from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import { AiToolBanner } from "@/components/marketing/AiGeneratorPage";
import { alternateLinkList } from "@/lib/i18n/locale";
import heroAsset from "@/assets/platform/scout/hero.webp.asset.json";
import searchAsset from "@/assets/platform/scout/search.webp.asset.json";
import outreachAsset from "@/assets/platform/scout/outreach.webp.asset.json";

const heroImg = heroAsset.url;
const searchImg = searchAsset.url;
const outreachImg = outreachAsset.url;

const heroAlt =
  "Banana Scout AI trên MacBook hỏi 'Hôm nay chúng ta scout doanh nghiệp nào?' — ô tìm kiếm ngôn ngữ tự nhiên cho lead B2B tại Việt Nam";
const searchAlt =
  "Kết quả Scout hiển thị 55 công ty bất động sản tại Hà Nội từ Google Places kèm đánh giá, số review và liên hệ, tinh chỉnh qua trò chuyện AI và xem trên iPhone";
const outreachAlt =
  "Scout trên iPhone với menu thêm liên hệ, đẩy vào Sales Pipeline và tạo task theo dõi với biến prospect name và địa chỉ";

export const Route = createFileRoute("/vi/platform/scout")({
  head: () => ({
    meta: [
      { title: "Scout — Tìm kiếm lead bằng AI — banana.cards" },
      { name: "description", content: "AI tìm và qualify lead cho bạn — dựa trên hồ sơ khách hàng lý tưởng và signals." },
      { property: "og:title", content: "Scout — banana.cards" },
      { property: "og:description", content: "AI tìm và qualify lead cho bạn." },
      { property: "og:image", content: heroImg },
      { property: "og:locale", content: "vi_VN" },
    ],
    links: alternateLinkList("/platform/scout", "vi"),
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="Scout"
      title="AI tìm cho bạn"
      italic="lead tiếp theo."
      topBanner={<AiToolBanner to="/ai/lead-scout" label="Thử AI Lead Scout — miễn phí" sublabel="Mô tả ICP, nhận danh sách lead phù hợp trong vài giây." />}
      description="Mô tả khách hàng lý tưởng. Scout tìm công ty và liên hệ phù hợp — đầy đủ thông tin liên lạc và signal mua hàng."
      heroImage={heroImg}
      heroImageAlt={heroAlt}
      metrics={[{ v: "10M+", l: "công ty được lập chỉ mục" }, { v: "85%", l: "email hợp lệ" }, { v: "AI", l: "scoring" }]}
      bullets={[
        { icon: Binoculars, title: "Tìm theo ICP", desc: "Mô tả ngành, kích thước, công nghệ — Scout tìm phù hợp." },
        { icon: Sparkles, title: "Chấm điểm bằng AI", desc: "Mỗi lead được chấm điểm theo độ phù hợp và signal intent." },
        { icon: Filter, title: "Lọc thông minh", desc: "Lọc theo công nghệ stack, tin tức gần đây, funding và hơn." }
      ]}
      deepDives={[
        { eyebrow: "Khám phá", title: "Tìm công ty,", italic: "tìm con người.", description: "Scout duyệt 10M+ công ty với signal cập nhật — bạn không bao giờ thiếu lead.", image: searchImg, imageAlt: searchAlt, reverse: false, points: ["Database 10M+ công ty", "Tìm theo ngành, size, tech stack", "Signal: funding, hiring, news", "Liên hệ với email được verify"] },
        { eyebrow: "Workflow", title: "Từ phát hiện", italic: "đến outreach.", description: "Khi tìm thấy lead phù hợp, đẩy thẳng vào CRM, gán cho rep, và bắt đầu sequence outreach.", image: outreachImg, imageAlt: outreachAlt, reverse: true, points: ["Đẩy vào CRM với một click", "Auto-tag và auto-segment", "Tích hợp với automation", "Theo dõi engagement"] }
      ]}
      howItWorks={[
        { title: "Mô tả ICP", desc: "Ngành, size, location, tech stack." },
        { title: "Scout tìm", desc: "AI duyệt và chấm điểm." },
        { title: "Duyệt list", desc: "Xem chi tiết và signal." },
        { title: "Đẩy vào CRM", desc: "Bắt đầu outreach." }
      ]}
      capabilities={["Database 10M+ công ty", "Email được verify", "Tech stack detection", "Signal: funding, hiring, news", "AI scoring", "Đẩy vào CRM", "Export CSV", "API access"]}
      faq={[
        { q: "Scout tìm lead ở đâu?", a: "Database công khai 10M+ công ty, được làm giàu với data partners. Tập trung vào B2B." },
        { q: "Email có hợp lệ không?", a: "~85% email được verify trước khi cung cấp. Email không verify được đánh dấu rõ ràng." },
        { q: "Có tuân thủ GDPR không?", a: "Có — chỉ data công khai, có thể xóa theo yêu cầu, opt-out tự động cho EU." },
        { q: "Khác biệt với ZoomInfo/Apollo?", a: "Scout tích hợp sâu với CRM Banana của bạn, AI chấm điểm theo ICP cụ thể, và rẻ hơn đáng kể với gói chỉ pay-as-you-go." }
      ]}
      outro={{ title: "Ngừng săn lead.", italic: "Để AI làm.", description: "Scout tìm cho bạn — bạn chốt deal." }}
    />
  );
}